import { useMemo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CEProductCustomizationForm } from '../product-customization-form/CEProductCustomizationForm';
import CEAddToCartButton from '../add-to-cart-button/CEAddToCartButton';
import DisplayAt from '../../components/DisplayAt';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization.jsx';
import { useTranslate } from '../../hooks/useTranslate.jsx';
import CECustomizationButton from '../customization-button/CECustomizationButton';
import CEProductFlipPreviewer from '../quick-buy/CEProductFlipPreviewer';
import CEProductPreviewer from '../quick-buy/CEProductPreviewer';
import { CEProductCustomizationPreview } from '../../bitf-customization-engine/CEProductCustomizationPreview';
import CEProductCustomizationPrice from '../product-customization-price/CEProductCustomizationPrice';

export default function CEProductCustomizationPdp({
  product,
  storeName = 'product-customization-pdp',
  watchStoreChanges = false,
  language,
  locale,
  currencyCode,
  country,
  market,
  localization,
  productHandle,
  productType,
  playerHandle,
  competitionHandle,
  showProductImage = true,
  isAlwaysVisible = true,
  isShopByPlayer = false,
  isPreorder = false,
  preorderLabel,
  attentionMessage,
}) {
  const normalizedProduct = product ?? null;
  const normalizedProductHandle = productHandle ?? normalizedProduct?.handle ?? null;
  const normalizedProductType = (
    productType ??
    normalizedProduct?.metafields?.custom?.product_type ??
    'jersey'
  ).toLowerCase();

  const resolvedLocalization = useMemo(() => {
    const targetLocale = localization?.locale ?? locale ?? language ?? null;
    const targetCountry = localization?.country ?? country ?? null;
    const targetMarket = localization?.market ?? market ?? null;

    if (targetLocale || targetCountry || targetMarket) {
      return {
        locale: targetLocale ?? undefined,
        country: targetCountry ?? undefined,
        market: targetMarket ?? undefined,
      };
    }

    return undefined;
  }, [localization, locale, language, country, market]);

  const customizationHookConfig = useMemo(
    () => ({
      storeName,
      watchStoreChanges,
      language: language ?? locale ?? resolvedLocalization?.locale,
      country,
      market,
      localization: resolvedLocalization,
      isShopByPlayer,
    }),
    [storeName, watchStoreChanges, language, locale, resolvedLocalization, country, market, isShopByPlayer],
  );
  const { store, resetCustomization } = useCEProductCustomization(customizationHookConfig);
  const { translate } = useTranslate(customizationHookConfig.language);
  const isInitialRender = useRef(true);
  const addToCartButtonText = isPreorder
    ? preorderLabel || 'Pre-order'
    : translate('cart.add_to_cart') || 'Add to cart';

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    const hasNonRequiredAddOn =
      store.currentAddOns !== null && store.currentAddOns?.some((addOn) => !addOn.customizationAddOnRequired);

    if (store.currentCustomizationType !== null || hasNonRequiredAddOn) {
      const customizableThumbnail = document.querySelector('[data-image-thumbnail-customizable]');
      customizableThumbnail?.click();
    }
  }, [store.currentCustomizationType, store.currentAddOns]);

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const mobileDrawerId = 'product-customization-drawer';
  const selectedVariant = store?.currentProductVariant;
  const ghostNameInputRef = useRef(null);
  const ghostNumberInputRef = useRef(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isMobileDrawerOpen) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.touchAction = 'none';
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
      body.style.touchAction = '';
    }

    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
      body.style.touchAction = '';
    };
  }, [isMobileDrawerOpen]);

  // Reset function for after successful add to cart
  const handleAddToCartSuccess = () => {
    resetCustomization();
    setIsMobileDrawerOpen(false);
  };

  if (!normalizedProductHandle) {
    return <div className="product-customization-pdp__empty">Product data is unavailable.</div>;
  }

  if (store.isLoadingCustomization !== false) {
    return (
      <div className="product-customization-form-skeleton" aria-hidden="true">
        <div className="product-customization-form-skeleton-row-thin" aria-hidden="true"></div>
        <div className="product-customization-form-skeleton-row" aria-hidden="true"></div>

        <div className="product-customization-form-skeleton-row-thin" aria-hidden="true"></div>
        <div className="product-customization-form-skeleton-row" aria-hidden="true"></div>

        <div className="product-customization-form-skeleton-row-thin" aria-hidden="true"></div>
        <div className="product-customization-form-skeleton-row" aria-hidden="true"></div>

        <div className="product-customization-form-skeleton-row-thin" aria-hidden="true"></div>
        <div className="product-customization-form-skeleton-row" aria-hidden="true"></div>
        <div className="product-customization-form-skeleton-button" aria-hidden="true"></div>
      </div>
    );
  }

  return store.currentProductVariant?.available ? (
    <>
      <DisplayAt value="desktop">
        <div className="product-customization-pdp__container">
          <p className="product-customization-pdp__title">{translate(`customization.form.customize`)}</p>
          <div className="product-customization__form-container">
            <CEProductCustomizationForm
              customizationHookConfig={customizationHookConfig}
              showCompetionLabel={true}
              formVariant="pdp"
            />
          </div>

          <div className="product-customization-pdp__footer">
            {attentionMessage && <div className="product-customization-pdp__attention-message">{attentionMessage}</div>}
            <CEAddToCartButton
              storeName={storeName}
              customizationHookConfig={customizationHookConfig}
              extraClassName="w-full"
              language={language ?? locale}
              buttonText={addToCartButtonText}
            />
          </div>
        </div>
      </DisplayAt>
      <DisplayAt value="mobile">
        <div className="product-customization-pdp__container">
          <div className="product-customization-pdp__footer">
            <button
              className="quick-buy__mobile-button ce-customize__text ProductPopup__OpenButton Button Button--secondary w-full"
              aria-controls={mobileDrawerId}
              disabled={!selectedVariant}
              onClick={() => setIsMobileDrawerOpen(!isMobileDrawerOpen)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M7.24219 16.875H3.75C3.58424 16.875 3.42527 16.8091 3.30806 16.6919C3.19085 16.5747 3.125 16.4158 3.125 16.25V12.7578C3.12472 12.6766 3.14044 12.5962 3.17128 12.5212C3.20211 12.4461 3.24745 12.3778 3.30469 12.3203L12.6797 2.94531C12.7378 2.88625 12.8072 2.83936 12.8836 2.80735C12.9601 2.77534 13.0421 2.75885 13.125 2.75885C13.2079 2.75885 13.2899 2.77534 13.3664 2.80735C13.4428 2.83936 13.5122 2.88625 13.5703 2.94531L17.0547 6.42968C17.1137 6.48784 17.1606 6.55716 17.1927 6.63361C17.2247 6.71006 17.2411 6.79211 17.2411 6.87499C17.2411 6.95787 17.2247 7.03993 17.1927 7.11638C17.1606 7.19283 17.1137 7.26215 17.0547 7.3203L7.67969 16.6953C7.62216 16.7526 7.55391 16.7979 7.47884 16.8287C7.40376 16.8596 7.32335 16.8753 7.24219 16.875Z"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path d="M10.625 5L15 9.375" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
                <path
                  d="M12.8125 7.1875L5.3125 14.6875"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.46094 16.8359L3.16406 12.5391"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {translate('customization.form.customize')}
            </button>

            <CEAddToCartButton
              storeName={storeName}
              customizationHookConfig={customizationHookConfig}
              extraClassName="w-full"
              language={language ?? locale}
              buttonText={addToCartButtonText}
            />
          </div>

          {/* Mobile Drawer */}
          {createPortal(
            <div
              id={mobileDrawerId}
              className={`product-customization-drawer product-customization-drawer-quick-buy ${isMobileDrawerOpen ? 'product-customization-drawer--open' : 'product-customization-drawer--closed'}`}
            >
              <div className="product-customization-drawer__container">
                <div className="product-customization-drawer__header">
                  <button onClick={() => setIsMobileDrawerOpen(false)} className="tap-area" aria-label="Close drawer">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
                      <g clip-path="url(#clip0_13470_30335)">
                        <path d="M15 14L1 0M15 0L1 14L15 0Z" stroke="black" />
                      </g>
                      <defs>
                        <clipPath id="clip0_13470_30335">
                          <rect width="16" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
                <div className="product-customization-drawer__product-preview" style={{ position: 'relative' }}>
                  <input
                    ref={ghostNameInputRef}
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    aria-hidden="true"
                    tabIndex={-1}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '1px',
                      height: '0px',
                      opacity: 0,
                      pointerEvents: 'none',
                      border: 'none',
                      outline: 'none',
                      padding: 0,
                      fontSize: '16px',
                      caretColor: 'transparent',
                    }}
                  />
                  <input
                    ref={ghostNumberInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    aria-hidden="true"
                    tabIndex={-1}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '1px',
                      height: '0px',
                      opacity: 0,
                      pointerEvents: 'none',
                      border: 'none',
                      outline: 'none',
                      padding: 0,
                      fontSize: '16px',
                      caretColor: 'transparent',
                    }}
                  />
                  <div className="product-customization-drawer__product-preview-content">
                    <div className="quick-buy__preview">
                      <CEProductFlipPreviewer
                        store={store}
                        frontContent={
                          <CEProductPreviewer
                            selectedProduct={product}
                            selectedVariant={selectedVariant}
                            frontImage={store.currentProduct?.customizationFrontImage?.image?.url}
                          />
                        }
                        backContent={
                          <CEProductCustomizationPreview
                            customizationHookConfig={{
                              storeName: storeName,
                              watchStoreChanges: false,
                              language: localization?.locale,
                            }}
                            showProductImage={true}
                            productType={normalizedProductType}
                            mixBlendMode={true}
                            productVariant={selectedVariant}
                          />
                        }
                        showFlipButtonsOutsidePreview={true}
                      />
                    </div>
                  </div>
                </div>

                <div className="product-customization-drawer__form">
                  <div className="ce__drawer-steps" role="tablist" aria-label="Quick buy steps"></div>

                  <div className="ce__drawer-step-content">
                    <div className={`ce__drawer-step-panel ce__drawer-step-panel--visible`}>
                      <div className="quick-buy__form-card quick-buy__form-card-inner">
                        <CEProductCustomizationForm
                          customizationHookConfig={customizationHookConfig}
                          showToggle={false}
                          showCompetionLabel={true}
                          ghostNameInputRef={ghostNameInputRef}
                          ghostNumberInputRef={ghostNumberInputRef}
                          formVariant="quick-buy"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="product-customization-drawer__footer">
                  <div className="ce__drawer-footer-main">
                    <div className="ce__drawer-total">
                      <span className="ce__drawer-total-label">Total</span>
                      <CEProductCustomizationPrice
                        storeName={storeName}
                        language={localization?.locale}
                        locale={localization?.locale}
                        currencyCode={currencyCode}
                        showOnlyPrice={true}
                        extraPriceClassName="ce__drawer-total-price"
                      />
                    </div>

                    <div className="ce__drawer-footer-action">
                      <CEAddToCartButton
                        storeName={storeName}
                        onAddToCartSuccess={handleAddToCartSuccess}
                        language={localization?.locale}
                        buttonText={addToCartButtonText}
                        extraClassName="ce__drawer-add-to-cart-content"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
        </div>
      </DisplayAt>
    </>
  ) : (
    <div className="product-customization-pdp__footer">
      <CEAddToCartButton
        storeName={storeName}
        customizationHookConfig={customizationHookConfig}
        extraClassName="w-full"
        language={language ?? locale}
        buttonText={addToCartButtonText}
      />
    </div>
  );
}
